import { DEG, wrapTwoPi } from "./time";

/** Pole of a body tilted by `axialTiltDeg` about ecliptic X, used when no IAU pole is known. */
export function tiltPole(axialTiltDeg: number | null, out: Float64Array, offset: number): void {
  const tilt = (axialTiltDeg ?? 0) * DEG;
  out[offset] = 0;
  out[offset + 1] = -Math.sin(tilt);
  out[offset + 2] = Math.cos(tilt);
}

/**
 * q = align(Z -> pole) * qz(spin), written as x,y,z,w. `align` is the shortest rotation taking
 * ecliptic north onto the unit pole at poles[poleOffset..+2]. Identity when rotation data is missing.
 */
export function spinQuaternion(
  rotationPeriodHours: number | null,
  poles: Float64Array,
  poleOffset: number,
  jd: number,
  epochJd: number,
  out: Float32Array,
  offset: number,
): void {
  if (rotationPeriodHours === null || rotationPeriodHours === 0) {
    out[offset] = 0;
    out[offset + 1] = 0;
    out[offset + 2] = 0;
    out[offset + 3] = 1;
    return;
  }
  let spin = wrapTwoPi((((jd - epochJd) * 24) / rotationPeriodHours) * 2 * Math.PI);
  // keep w >= 0 so q and -q never alternate across a wrap
  if (spin > Math.PI) spin -= 2 * Math.PI;
  const px = poles[poleOffset],
    py = poles[poleOffset + 1],
    pz = poles[poleOffset + 2];
  // axis = Z x pole = (-py, px, 0), |axis| = sin(angle); half angles from cos(angle) = pz
  const c = Math.min(Math.max(pz, -1), 1);
  const sa = Math.sqrt((1 - c) / 2),
    ca = Math.sqrt((1 + c) / 2);
  const len = Math.hypot(px, py);
  let ux = 1,
    uy = 0; // antiparallel: half turn about X; parallel: sa = 0 makes the axis irrelevant
  if (len > 1e-12) {
    ux = -py / len;
    uy = px / len;
  }
  const sz = Math.sin(spin / 2),
    cz = Math.cos(spin / 2);
  // (ca + sa (ux i + uy j)) * (cz + sz k); i k = -j, j k = i
  out[offset] = sa * (ux * cz + uy * sz);
  out[offset + 1] = sa * (uy * cz - ux * sz);
  out[offset + 2] = ca * sz;
  out[offset + 3] = ca * cz;
}
